import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConfirmSaleRequest } from '@pms/shared';
import {
  confirmSale,
  getPosProducts,
  getPosSettings,
  getReceipt,
  type PosProductsQuery,
} from '@/services/api/posSales.api';

/** In-stock products for the terminal grid. */
export function usePosProducts(query: PosProductsQuery, enabled = true) {
  return useQuery({
    queryKey: ['pos-products', query],
    queryFn: () => getPosProducts(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

/** POS settings (tax rate) for the optimistic cart total. */
export function usePosSettings(propertyId: string, enabled = true) {
  return useQuery({
    queryKey: ['pos-settings', propertyId],
    queryFn: () => getPosSettings(propertyId),
    staleTime: 30_000,
    enabled: enabled && !!propertyId,
  });
}

/** Canonical receipt fetch — backs reprint. */
export function usePosReceipt(id: string | null) {
  return useQuery({
    queryKey: ['pos-sale', id],
    queryFn: () => getReceipt(id as string),
    enabled: !!id,
  });
}

/**
 * Tender mutation. The caller mints one `idempotencyKey` (UUID) per tender
 * attempt and reuses it across raw retries; the sale invalidates both the
 * terminal grid and the stock list since on-hand changed.
 */
export function useConfirmSale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      payload,
      idempotencyKey,
    }: {
      payload: ConfirmSaleRequest;
      idempotencyKey: string;
    }) => confirmSale(payload, idempotencyKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pos-products'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-stock'] });
    },
  });
}
